import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, SITE_NAME, structuredDataFor } from '../seoConfig';

function ensureMetaByName(name) {
  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('name', name);
    document.head.appendChild(element);
  }

  return element;
}

function ensureMetaByProperty(property) {
  let element = document.querySelector(`meta[property="${property}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }

  return element;
}

function ensureCanonicalLink() {
  let element = document.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  return element;
}

function upsertStructuredData(meta) {
  // Reuse the script element the server injects so there is only ever a single
  // route-level JSON-LD block. Creating a separate element here would leave the
  // server-rendered one in place and produce duplicate entities (e.g. FAQPage).
  const scriptId = 'server-route-structured-data';
  const legacyScript = document.getElementById('route-structured-data');

  if (legacyScript) {
    legacyScript.remove();
  }

  const existingScript = document.getElementById(scriptId);
  const structuredData = structuredDataFor(meta);

  if (!structuredData) {
    if (existingScript) {
      existingScript.remove();
    }
    return;
  }

  const script = existingScript || document.createElement('script');
  script.id = scriptId;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);

  if (!existingScript) {
    document.head.appendChild(script);
  }
}

function SEO() {
  const location = useLocation();

  useEffect(() => {
    const meta = getSeoForPath(location.pathname);
    const title = meta.title.includes(SITE_NAME) ? meta.title : `${meta.title} | ${SITE_NAME}`;

    document.title = title;
    ensureMetaByName('description').setAttribute('content', meta.description);
    ensureMetaByName('robots').setAttribute('content', meta.robots);
    ensureMetaByName('keywords').setAttribute('content', meta.keywords);
    ensureMetaByName('application-name').setAttribute('content', SITE_NAME);
    ensureCanonicalLink().setAttribute('href', meta.canonical);

    ensureMetaByProperty('og:site_name').setAttribute('content', SITE_NAME);
    ensureMetaByProperty('og:type').setAttribute('content', 'website');
    ensureMetaByProperty('og:title').setAttribute('content', title);
    ensureMetaByProperty('og:description').setAttribute('content', meta.description);
    ensureMetaByProperty('og:url').setAttribute('content', meta.canonical);
    ensureMetaByProperty('og:image').setAttribute('content', meta.image);

    ensureMetaByName('twitter:card').setAttribute('content', 'summary_large_image');
    ensureMetaByName('twitter:title').setAttribute('content', title);
    ensureMetaByName('twitter:description').setAttribute('content', meta.description);
    ensureMetaByName('twitter:image').setAttribute('content', meta.image);

    upsertStructuredData(meta);
  }, [location.pathname]);

  return null;
}

export default SEO;
